import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import assetDisposalService, {
  AssetDetail,
  DisposalDocument,
  DisposalRequest,
  DisposalRequestParams
} from '../../services/assetDisposalService';
import { RootState } from '../store';
import { Asset, assetApi } from '../api/assetApi';
import { UserDropDown } from '@/types/user.types';

// Error response interface
export interface ErrorResponse {
  status?: number;
  code?: string;
  message?: string;
}

export interface DisposalItemDetail {
  id: string;
  disposal_date: string;
  sold_value: string | number;
  calculated_depreciation_value: string;
  reason: string;
  comments: string;
  asset_disposal_request: string;
  asset: string;
  document: string | null;
  asset_detail?: Asset;
  // Supporting documents are attached per disposal detail (one row per asset)
  documents_detail?: DocumentItem[];
}

export interface DisposalFormDetail {
  reason: string;
  asset_id: string;
  comments: string;
  asset_name: string;
  sold_value: number;
  disposal_date: string;
  purchase_value: number;
  calculated_depreciation_value: number;
}

export interface DisposalFormData {
  status: string;
  details: DisposalFormDetail[];
  created_by: string;
  asset_count: number;
  disposal_request_id: string;
}

export interface WorkflowInstanceDetail {
  id: string;
  transaction_id: string;
  workflow_template: {
    id: string;
    name: string;
  };
  initiator: {
    id: string;
    name: string;
  };
  status: string;
  status_display: string;
  current_approval_level: number;
  form_data: DisposalFormData;
  reference_object_type: string;
  reference_object_id: string;
  submission_date: string;
  estimated_completion_date: string | null;
  completion_date: string;
}

export interface CreatedBy {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  company: string;
  company_name: string;
  department: string | null;
  reporting_manager: string;
  reporting_manager_name: string;
}

export interface DisposalDetail {
  id: string;
  status: string;
  status_display: string;
  workflow_instance: string;
  workflow_instance_detail: WorkflowInstanceDetail;
  created_by: string;
  created_by_detail: CreatedBy;
  created_at: string;
  updated_at: string;
  item_details: DisposalItemDetail;
}

export interface AssetWithDisposal extends Asset {
  disposal_detail: DisposalDetail;
}

export interface DisposedAssets {
  count: number;
  next: string | null;
  previous: string | null;
  results: AssetWithDisposal[];
}

export interface DocumentItem {
  id: string;
  content_type: number;
  object_id: string;
  file_type: string;
  filename: string;
  file_size: number;
  checksum_md5: string;
  status: string;
  created_at: string;
  updated_at: string;
  uploaded_by: string;
}

export interface DisposalAssetDetails {
  created_at: string;
  created_by: string;
  created_by_detail: UserDropDown | null;
  id: string;
  status: string;
  status_display: string;
  updated_at: string;
  workflow_instance: null | string;
  workflow_instance_detail: null | WorkflowInstanceDetail;
}

// Type for list endpoint - per-asset detail rows
export interface DisposalRequestListItem extends DisposalItemDetail {
  asset_disposal_request_detail: DisposalAssetDetails;
  asset_detail: Asset;
  documents_detail?: DocumentItem[];
}

export interface DisposalRequests {
  count: number;
  next: string | null;
  previous: string | null;
  results: DisposalRequestListItem[];
}

// Type for single request endpoint - request header with details array
export interface DisposalRequestDetail {
  id: string;
  status: string;
  status_display: string;
  workflow_instance: string | null;
  workflow_instance_detail: WorkflowInstanceDetail | null;
  created_by: string;
  created_by_detail: CreatedBy;
  created_at: string;
  updated_at: string;
  details: DisposalItemDetail[];
  documents?: DocumentItem[];
  custom_fields?: Record<string, any>;
  custom_fields_detail?: Record<string, any>;
}

// Types
interface AssetDisposalState {
  disposalRequests: {
    loading: boolean;
    data: DisposalRequestListItem[];
    error: ErrorResponse | null;
    pagination: {
      count: number;
      next: string | null;
      previous: string | null;
    };
  };
  disposalRequest: {
    loading: boolean;
    data: DisposalRequestDetail | null;
    error: ErrorResponse | null;
  };
  disposedAssets: {
    loading: boolean;
    data: AssetWithDisposal[];
    error: ErrorResponse | null;
    pagination: {
      count: number;
      next: string | null;
      previous: string | null;
    };
  };
  disposalDocuments: {
    loading: boolean;
    data: DisposalDocument[];
    error: ErrorResponse | null;
  };
  createDisposalRequest: {
    loading: boolean;
    error: ErrorResponse | null;
  };
  updateDisposalRequest: {
    loading: boolean;
    error: ErrorResponse | null;
  };
  deleteDisposalRequest: {
    loading: boolean;
    error: ErrorResponse | null;
  };
  selectedAssets: AssetDetail[];
  currentPage: number;
  pageSize: number;
}

// Initial state
const initialState: AssetDisposalState = {
  disposalRequests: {
    loading: false,
    data: [],
    error: null,
    pagination: {
      count: 0,
      next: null,
      previous: null
    }
  },
  disposalRequest: {
    loading: false,
    data: null,
    error: null
  },
  disposedAssets: {
    loading: false,
    data: [],
    error: null,
    pagination: {
      count: 0,
      next: null,
      previous: null
    }
  },
  disposalDocuments: {
    loading: false,
    data: [],
    error: null
  },
  createDisposalRequest: {
    loading: false,
    error: null
  },
  updateDisposalRequest: {
    loading: false,
    error: null
  },
  deleteDisposalRequest: {
    loading: false,
    error: null
  },
  selectedAssets: [],
  currentPage: 1,
  pageSize: 10
};

// Async thunks
export const fetchDisposalRequests = createAsyncThunk(
  'assetDisposal/fetchDisposalRequests',
  async (params: DisposalRequestParams = {}, { rejectWithValue }) => {
    try {
      const response = await assetDisposalService.getDisposalRequests(params);
      return response.data;
    } catch (error) {
      const err = error as { response?: { data: ErrorResponse }; message: string };
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const fetchDisposalRequestById = createAsyncThunk(
  'assetDisposal/fetchDisposalRequestById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await assetDisposalService.getDisposalRequest(id);
      return response.data;
    } catch (error) {
      const err = error as { response?: { data: ErrorResponse }; message: string };
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const createDisposalRequest = createAsyncThunk(
  'assetDisposal/createDisposalRequest',
  async (formdata:FormData, { rejectWithValue, dispatch }) => {
    try {
      const response = await assetDisposalService.createDisposalRequest(formdata);
      dispatch(assetApi.util.invalidateTags(['Asset']));
      return response.data;
    } catch (error) {
      const err = error as { response?: { data: ErrorResponse }; message: string };
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const createBulkDisposalRequest = createAsyncThunk(
  'assetDisposal/createBulkDisposalRequest',
  async (data: DisposalRequest, { rejectWithValue, dispatch }) => {
    try {
      const response = await assetDisposalService.createBulkDisposalRequest(data);
      dispatch(assetApi.util.invalidateTags(['Asset']));
      return response.data;
    } catch (error) {
      const err = error as { response?: { data: ErrorResponse }; message: string };
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const updateDisposalRequest = createAsyncThunk(
  'assetDisposal/updateDisposalRequest',
  async ({ id, data }: { id: string; data: Partial<DisposalRequest> }, { rejectWithValue }) => {
    try {
      const response = await assetDisposalService.updateDisposalRequest(id, data);
      return response.data;
    } catch (error) {
      const err = error as { response?: { data: ErrorResponse }; message: string };
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const deleteDisposalRequest = createAsyncThunk(
  'assetDisposal/deleteDisposalRequest',
  async (id: string, { rejectWithValue }) => {
    try {
      await assetDisposalService.deleteDisposalRequest(id);
      return id;
    } catch (error) {
      const err = error as { response?: { data: ErrorResponse }; message: string };
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const fetchDisposedAssets = createAsyncThunk(
  'assetDisposal/fetchDisposedAssets',
  async (params: DisposalRequestParams = {}, { rejectWithValue }) => {
    try {
      const response = await assetDisposalService.getDisposedAssets(params);
      return response.data;
    } catch (error) {
      const err = error as { response?: { data: ErrorResponse }; message: string };
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const uploadDisposalDocument = createAsyncThunk(
  'assetDisposal/uploadDisposalDocument',
  async ({ id, formData }: { id: string; formData: FormData }, { rejectWithValue }) => {
    try {
      const response = await assetDisposalService.uploadDisposalDocuments(id, formData);
      return response.data;
    } catch (error) {
      const err = error as { response?: { data: ErrorResponse }; message: string };
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const fetchDisposalDocuments = createAsyncThunk(
  'assetDisposal/fetchDisposalDocuments',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await assetDisposalService.getDisposalDocuments(id);
      return response.data;
    } catch (error) {
      const err = error as { response?: { data: ErrorResponse }; message: string };
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const approveDisposalRequest = createAsyncThunk(
  'assetDisposal/approveDisposalRequest',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await assetDisposalService.approveDisposalRequest(id);
      return response.data;
    } catch (error) {
      const err = error as { response?: { data: ErrorResponse }; message: string };
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

export const rejectDisposalRequest = createAsyncThunk(
  'assetDisposal/rejectDisposalRequest',
  async ({ id, reason }: { id: string; reason: string }, { rejectWithValue }) => {
    try {
      const response = await assetDisposalService.rejectDisposalRequest(id, reason);
      return response.data;
    } catch (error) {
      const err = error as { response?: { data: ErrorResponse }; message: string };
      return rejectWithValue(err.response?.data || { message: err.message });
    }
  }
);

// Slice
const assetDisposalSlice = createSlice({
  name: 'assetDisposal',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    addSelectedAsset: (state, action: PayloadAction<AssetDetail>) => {
      if (!state.selectedAssets.some(asset => asset.id === action.payload.id)) {
        state.selectedAssets.push(action.payload);
      }
    },
    removeSelectedAsset: (state, action: PayloadAction<string>) => {
      state.selectedAssets = state.selectedAssets.filter(asset => asset.id !== action.payload);
    },
    clearSelectedAssets: (state) => {
      state.selectedAssets = [];
    },
    resetDisposalState: (state) => {
      state.disposalRequest.data = null;
      state.disposalRequest.error = null;
      state.disposalDocuments.data = [];
      state.disposalDocuments.error = null;
      state.createDisposalRequest.error = null;
      state.updateDisposalRequest.error = null;
      state.deleteDisposalRequest.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchDisposalRequests
      .addCase(fetchDisposalRequests.pending, (state) => {
        state.disposalRequests.loading = true;
        state.disposalRequests.error = null;
      })
      .addCase(fetchDisposalRequests.fulfilled, (state, action) => {
        state.disposalRequests.loading = false;
        
        // Handle different response formats
        if (Array.isArray(action.payload)) {
          state.disposalRequests.data = action.payload;
          state.disposalRequests.pagination = {
            count: action.payload.length,
            next: null,
            previous: null
          };
        } else if (action.payload && typeof action.payload === 'object') {
          if ('results' in action.payload && Array.isArray(action.payload.results)) {
            state.disposalRequests.data = action.payload.results;
            state.disposalRequests.pagination = {
              count: action.payload.count || 0,
              next: action.payload.next || null,
              previous: action.payload.previous || null
            };
          } else {
            // Try to convert the object to an array if it's not in the expected format
            const dataArray = Object.values(action.payload).filter(item =>
              item && typeof item === 'object' && 'id' in item
            );
            
            if (dataArray.length > 0) {
              state.disposalRequests.data = dataArray as DisposalRequest[];
              state.disposalRequests.pagination = {
                count: dataArray.length,
                next: null,
                previous: null
              };
            } else {
              state.disposalRequests.data = [];
              state.disposalRequests.pagination = {
                count: 0,
                next: null,
                previous: null
              };
            }
          }
        } else {
          state.disposalRequests.data = [];
          state.disposalRequests.pagination = {
            count: 0,
            next: null,
            previous: null
          };
        }
      })
      .addCase(fetchDisposalRequests.rejected, (state, action) => {
        state.disposalRequests.loading = false;
        state.disposalRequests.error = action.payload as ErrorResponse;
      })
      
      // fetchDisposalRequestById
      .addCase(fetchDisposalRequestById.pending, (state) => {
        state.disposalRequest.loading = true;
        state.disposalRequest.error = null;
      })
      .addCase(fetchDisposalRequestById.fulfilled, (state, action) => {
        state.disposalRequest.loading = false;
        state.disposalRequest.data = action.payload || null;
        
        // If the response includes documents, update the documents state as well
        if (action.payload?.documents) {
          state.disposalDocuments.data = action.payload.documents;
        }
      })
      .addCase(fetchDisposalRequestById.rejected, (state, action) => {
        state.disposalRequest.loading = false;
        state.disposalRequest.error = action.payload as ErrorResponse;
      })
      
      // createDisposalRequest
      .addCase(createDisposalRequest.pending, (state) => {
        state.createDisposalRequest.loading = true;
        state.createDisposalRequest.error = null;
      })
      .addCase(createDisposalRequest.fulfilled, (state, action) => {
        state.createDisposalRequest.loading = false;
        state.disposalRequest.data = action.payload;
      })
      .addCase(createDisposalRequest.rejected, (state, action) => {
        state.createDisposalRequest.loading = false;
        state.createDisposalRequest.error = action.payload as ErrorResponse;
      })
      
      // createBulkDisposalRequest
      .addCase(createBulkDisposalRequest.pending, (state) => {
        state.createDisposalRequest.loading = true;
        state.createDisposalRequest.error = null;
      })
      .addCase(createBulkDisposalRequest.fulfilled, (state) => {
        state.createDisposalRequest.loading = false;
        state.selectedAssets = [];
      })
      .addCase(createBulkDisposalRequest.rejected, (state, action) => {
        state.createDisposalRequest.loading = false;
        state.createDisposalRequest.error = action.payload as ErrorResponse;
      })
      
      // updateDisposalRequest
      .addCase(updateDisposalRequest.pending, (state) => {
        state.updateDisposalRequest.loading = true;
        state.updateDisposalRequest.error = null;
      })
      .addCase(updateDisposalRequest.fulfilled, (state, action) => {
        state.updateDisposalRequest.loading = false;
        
        // Update the current disposal request if it matches
        if (state.disposalRequest.data?.id === action.payload.id) {
          state.disposalRequest.data = action.payload;
        }
      })
      .addCase(updateDisposalRequest.rejected, (state, action) => {
        state.updateDisposalRequest.loading = false;
        state.updateDisposalRequest.error = action.payload as ErrorResponse;
      })
      
      // deleteDisposalRequest
      .addCase(deleteDisposalRequest.pending, (state) => {
        state.deleteDisposalRequest.loading = true;
        state.deleteDisposalRequest.error = null;
      })
      .addCase(deleteDisposalRequest.fulfilled, (state, action) => {
        state.deleteDisposalRequest.loading = false;
        
        // Clear current if it matches
        if (state.disposalRequest.data?.id === action.payload) {
          state.disposalRequest.data = null;
        }
      })
      .addCase(deleteDisposalRequest.rejected, (state, action) => {
        state.deleteDisposalRequest.loading = false;
        state.deleteDisposalRequest.error = action.payload as ErrorResponse;
      })
      
      // fetchDisposedAssets
      .addCase(fetchDisposedAssets.pending, (state) => {
        state.disposedAssets.loading = true;
        state.disposedAssets.error = null;
      })
      .addCase(fetchDisposedAssets.fulfilled, (state, action) => {
        state.disposedAssets.loading = false;
        state.disposedAssets.data = action.payload.results || action.payload;
        state.disposedAssets.pagination = {
          count: action.payload.count || 0,
          next: action.payload.next || null,
          previous: action.payload.previous || null
        };
      })
      .addCase(fetchDisposedAssets.rejected, (state, action) => {
        state.disposedAssets.loading = false;
        state.disposedAssets.error = action.payload as ErrorResponse;
      })
      
      // uploadDisposalDocument
      .addCase(uploadDisposalDocument.pending, (state) => {
        state.disposalDocuments.loading = true;
        state.disposalDocuments.error = null;
      })
      .addCase(uploadDisposalDocument.fulfilled, (state, action) => {
        state.disposalDocuments.loading = false;
        state.disposalDocuments.data.push(action.payload);
      })
      .addCase(uploadDisposalDocument.rejected, (state, action) => {
        state.disposalDocuments.loading = false;
        state.disposalDocuments.error = action.payload as ErrorResponse;
      })
      
      // fetchDisposalDocuments
      .addCase(fetchDisposalDocuments.pending, (state) => {
        state.disposalDocuments.loading = true;
        state.disposalDocuments.error = null;
      })
      .addCase(fetchDisposalDocuments.fulfilled, (state, action) => {
        state.disposalDocuments.loading = false;
        
        // Handle different response formats
        if (Array.isArray(action.payload)) {
          state.disposalDocuments.data = action.payload;
        } else if (action.payload && Array.isArray(action.payload.results)) {
          state.disposalDocuments.data = action.payload.results;
        } else if (action.payload && action.payload.documents && Array.isArray(action.payload.documents)) {
          state.disposalDocuments.data = action.payload.documents;
        } else if (action.payload && action.payload.id && Array.isArray(action.payload.documents)) {
          state.disposalDocuments.data = action.payload.documents;
          
          // Also update the disposal request documents if it's the same ID
          if (state.disposalRequest.data?.id === action.payload.id && state.disposalRequest.data.documents) {
            state.disposalRequest.data.documents = action.payload.documents;
          }
        } else {
          state.disposalDocuments.data = [];
        }
      })
      .addCase(fetchDisposalDocuments.rejected, (state, action) => {
        state.disposalDocuments.loading = false;
        state.disposalDocuments.error = action.payload as ErrorResponse;
      })
      
      // approveDisposalRequest
      .addCase(approveDisposalRequest.pending, (state) => {
        state.updateDisposalRequest.loading = true;
        state.updateDisposalRequest.error = null;
      })
      .addCase(approveDisposalRequest.fulfilled, (state, action) => {
        state.updateDisposalRequest.loading = false;
        
        // Update the current disposal request if it matches
        if (state.disposalRequest.data?.id === action.payload.id) {
          state.disposalRequest.data = action.payload;
        }
      })
      .addCase(approveDisposalRequest.rejected, (state, action) => {
        state.updateDisposalRequest.loading = false;
        state.updateDisposalRequest.error = action.payload as ErrorResponse;
      })
      
      // rejectDisposalRequest
      .addCase(rejectDisposalRequest.pending, (state) => {
        state.updateDisposalRequest.loading = true;
        state.updateDisposalRequest.error = null;
      })
      .addCase(rejectDisposalRequest.fulfilled, (state, action) => {
        state.updateDisposalRequest.loading = false;
        
        // Update the current disposal request if it matches
        if (state.disposalRequest.data?.id === action.payload.id) {
          state.disposalRequest.data = action.payload;
        }
      })
      .addCase(rejectDisposalRequest.rejected, (state, action) => {
        state.updateDisposalRequest.loading = false;
        state.updateDisposalRequest.error = action.payload as ErrorResponse;
      });
  }
});

// Actions
export const {
  setCurrentPage,
  setPageSize,
  addSelectedAsset,
  removeSelectedAsset,
  clearSelectedAssets,
  resetDisposalState
} = assetDisposalSlice.actions;

// Selectors with null checks
export const selectDisposalRequests = (state: RootState) => 
  state.assetDisposal ? state.assetDisposal.disposalRequests : initialState.disposalRequests;

export const selectDisposalRequest = (state: RootState) => 
  state.assetDisposal ? state.assetDisposal.disposalRequest : initialState.disposalRequest;

export const selectDisposedAssets = (state: RootState) => 
  state.assetDisposal ? state.assetDisposal.disposedAssets : initialState.disposedAssets;

export const selectDisposalDocuments = (state: RootState) => 
  state.assetDisposal ? state.assetDisposal.disposalDocuments : initialState.disposalDocuments;

export const selectCreateDisposalRequest = (state: RootState) => 
  state.assetDisposal ? state.assetDisposal.createDisposalRequest : initialState.createDisposalRequest;

export const selectUpdateDisposalRequest = (state: RootState) => 
  state.assetDisposal ? state.assetDisposal.updateDisposalRequest : initialState.updateDisposalRequest;

export const selectDeleteDisposalRequest = (state: RootState) => 
  state.assetDisposal ? state.assetDisposal.deleteDisposalRequest : initialState.deleteDisposalRequest;

export const selectSelectedAssets = (state: RootState) => 
  state.assetDisposal ? state.assetDisposal.selectedAssets : initialState.selectedAssets;

export const selectPagination = (state: RootState) => ({
  totalCount: state.assetDisposal ? state.assetDisposal.disposalRequests.pagination.count : 0,
  currentPage: state.assetDisposal ? state.assetDisposal.currentPage : 1,
  pageSize: state.assetDisposal ? state.assetDisposal.pageSize : 10
});

export type { DisposalRequestListItem, DisposalRequestDetail };
export default assetDisposalSlice.reducer;